// Libraries
import ExcelJS, { type Workbook, type Worksheet } from 'exceljs';
// Services
import { FocByFuelType, PeriodReportData, VesselCiiRatings } from '@services/index';
import { FleetRatingsColumnsBuilder } from './FleetRatingsColumnsBuilder';
// Constants
import { RangeDate } from '@stores/index';
import { Customers } from '@utils/constants';

/**
 * Class to build an Excel file with fleet ratings export data.
 * It creates a summary sheet and individual sheets for each vessel's daily data.
 *
 * @class BuildFleetRatingsExportFile
 * @extends FleetRatingsColumnsBuilder
 * @property {Workbook} workbook - The Excel workbook instance.
 *
 * @constructor
 * @param {VesselCiiRatings[]} vesselsCiiRatings - Array of vessel CII ratings data.
 * @param {RangeDate} initialDate - The initial date range for the report.
 * @param {RangeDate} selectedDate - The selected date range for the report.
 * @param {Customers} customerName - The name of the customer.
 * @param {number[]} selectedImos - Array of IMO numbers for the vessels to include in the report.
 *
 * @example
 * const exportFile = new BuildFleetRatingsExportFile(vesselsCiiRatings, initialDate, selectedDate, customerName, selectedImos);
 * exportFile.workbook.xlsx.writeFile('FleetRatingsExport.xlsx');
 *
 * @returns {BuildFleetRatingsExportFile} An instance of the class with the workbook ready for export.
 */
export class BuildFleetRatingsExportFile extends FleetRatingsColumnsBuilder {
  workbook: Workbook;
  protected customerName: string;

  /**
   * Creates an instance of BuildFleetRatingsExportFile.
   * Initializes the workbook and creates the summary and vessel sheets.
   */
  constructor(
    vesselsCiiRatings: VesselCiiRatings[],
    initialDate: RangeDate,
    selectedDate: RangeDate,
    customerName: Customers,
    selectedImos: number[]
  ) {
    const creationDate = new Date();
    const selectedVesselsCiiRatings = vesselsCiiRatings.filter(({ imo }) => selectedImos.includes(imo));
    super(selectedVesselsCiiRatings, initialDate, selectedDate, customerName);
    this.workbook = new ExcelJS.Workbook();
    this.customerName = customerName;
    this.workbook.creator = 'Bearing AI';
    this.workbook.lastModifiedBy = 'Bearing AI';
    this.workbook.created = creationDate;
    this.workbook.modified = creationDate;
    this.createSummarySheet();
    selectedVesselsCiiRatings.map((vesselCiiRatings) => {
      const sheetName = `${vesselCiiRatings.vesselName}_${vesselCiiRatings.imo}`;
      const worksheet = this.workbook.addWorksheet(sheetName, {
        views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }],
      });
      this.createVesselDailyDataSheet(worksheet, vesselCiiRatings);
      this.autoFitColumns(worksheet);
    });
  }

  /**
   * Creates the summary sheet in the workbook.
   * It sets up the columns, headers, and populates the data from vessel CII ratings.
   * The summary sheet includes vessel information, CII ratings, and fuel consumption data.
   *
   * @returns {void}
   */
  private createSummarySheet = () => {
    const summaryWorksheet = this.workbook.addWorksheet('Summary', {
      views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }],
    });

    this.summaryColumns.map(({ displayName }, columnIdx) => {
      summaryWorksheet.getColumn(columnIdx + 1).width = displayName.length + 2;
      const labelCel = summaryWorksheet.getCell(`${this.numberToCol(columnIdx + 1)}1`);
      Object.assign(labelCel, { value: displayName, font: { bold: true } });
    });

    this.vesselsCiiRatings.forEach((vesselCiiRatings, rowIdx) => {
      this.summaryColumns.forEach((summaryColumn, colIdx) => {
        let value = null;
        const columnId = summaryColumn.id as keyof VesselCiiRatings;
        if (summaryColumn.rootProp) {
          value = (
            vesselCiiRatings[summaryColumn.rootProp as keyof VesselCiiRatings] as Record<string, number | string>
          )?.[columnId];
        } else {
          value = vesselCiiRatings[columnId];
        }
        const valueCell = summaryWorksheet.getCell(`${this.numberToCol(colIdx + 1)}${rowIdx + 2}`);
        Object.assign(valueCell, { value, ...this.summaryColumns[colIdx].cellProps });
      });
    });

    this.autoFitColumns(summaryWorksheet);
  };

  /**
   * Creates a daily data sheet for each vessel in the workbook.
   * It populates the worksheet with daily reports based on the vessel's CII ratings.
   * The daily reports include fuel consumption data and other relevant metrics.
   * This method dynamically generates columns based on the fuel types present in the data.
   *
   * @param {Worksheet} worksheet - The Excel worksheet to populate with daily data.
   * @param {VesselCiiRatings} vesselCiiRatings - The CII ratings data for the vessel.
   *
   * @returns {void}
   */
  private createVesselDailyDataSheet = (worksheet: Worksheet, vesselCiiRatings: VesselCiiRatings) => {
    if (vesselCiiRatings.periodFocByFuelType && vesselCiiRatings.periodReportData) {
      const dailyReportColumns = this.getDailyReportsColumns(vesselCiiRatings.periodFocByFuelType);

      dailyReportColumns.forEach((dailyColumn, colIdx) => {
        const labelCel = worksheet.getCell(`${this.numberToCol(colIdx + 1)}1`);
        Object.assign(labelCel, { value: dailyColumn.displayName, font: { bold: true } });

        let values = [];
        if (dailyColumn.rootProp) {
          values =
            (vesselCiiRatings.periodReportData?.[dailyColumn.rootProp as keyof PeriodReportData] as FocByFuelType)?.[
              dailyColumn.id
            ] ?? [];
        } else {
          values = (vesselCiiRatings.periodReportData?.[dailyColumn.id as keyof PeriodReportData] as number[]) ?? [];
        }

        values.forEach((value, rowIdx) => {
          const formattedValue = dailyColumn.transform ? dailyColumn.transform(value) : value;
          const cleanValue = !formattedValue ? null : formattedValue;
          const valueCell = worksheet.getCell(`${this.numberToCol(colIdx + 1)}${rowIdx + 2}`);
          Object.assign(valueCell, {
            value: cleanValue,
            ...dailyColumn.cellProps,
          });
        });
      });
    }
  };

  /**
   * Converts a 1-based column number to its corresponding Excel column letter(s).
   *
   * @param num - Column index (1-based)
   * @returns Excel column letter(s)
   */
  private numberToCol = (num: number) => {
    let col = '';
    while (num > 0) {
      const remainder = (num - 1) % 26;
      col = String.fromCharCode(65 + remainder) + col;
      num = Math.floor((num - 1) / 26);
    }
    return col;
  };

  /**
   * Auto-fits the columns in the given worksheet based on the content.
   * It iterates through each column, calculates the maximum length of the content,
   * and sets the column width accordingly.
   *
   * @param {Worksheet} worksheet - The Excel worksheet to auto-fit columns for.
   *
   * @returns {void}
   */
  private autoFitColumns = (worksheet: ExcelJS.Worksheet) => {
    worksheet.columns?.forEach((column) => {
      let maxLength = 8;
      if (column.eachCell) {
        column.eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { ...cell.font, name: 'Consolas' };
          const cellText = cell.text;
          maxLength = Math.max(maxLength, cellText.length);
        });
        column.width = maxLength + 2;
      }
    });
  };
}
